import type { ReactNode } from "react";

/**
 * The table's column tracks, declared ONCE and shared by the header row and every
 * body row on BOTH parcel surfaces — the create modal's {@link ParcelRowsEditor}
 * and the detail view's in-place add/edit row.
 *
 * A scroll-body table whose header carries its own copy of the widths is the
 * classic way the two silently drift apart; a second surface re-declaring them is
 * how the two selection bars and the two pagination blocks drifted apart earlier
 * in this sprint. The trailing 32px track belongs to the row's trailing control.
 *
 * The weight track is 68px, not the 56px it started at: the detail view seeds its
 * rows from saved parcels, and a `number` input reserves room for its spinner on
 * top of the text. Measured on order 94 at 1280, «2.5» needed 58px of content in
 * a 54px content box and rendered clipped. The create modal never showed this
 * because its rows start empty.
 */
export const PARCEL_GRID_COLUMNS = "minmax(0,5fr) minmax(0,5fr) 68px 32px";

/**
 * Height of exactly FOUR rows — the owner's single scrolling exception.
 *
 * «Список мест — единственное, что может быть любой длины, поэтому только он
 * получает прокрутку с фиксированной высотой в 4 строки.» Everything else in the
 * dialog is bounded by its own content, so the height budget holds no matter how
 * many parcels a dispatcher enters; the list absorbs all of it.
 *
 * The number is derived, not transcribed: `h-9` (36px) per row + `gap-1.5` (6px)
 * between them = 4×36 + 3×6 = 162px. Keeping the arithmetic here means changing
 * the row height cannot silently desync the viewport from "4 rows".
 */
export const PARCEL_ROW_H = 36;
const ROW_GAP = 6;
/** Visible rows in the CREATE modal's list — the owner's «на размер 4х шт». */
export const PARCEL_VISIBLE_ROWS = 4;
export const PARCEL_LIST_MAX_H =
  PARCEL_ROW_H * PARCEL_VISIBLE_ROWS + ROW_GAP * (PARCEL_VISIBLE_ROWS - 1);

/**
 * Visible rows in the DETAIL view's list — two, not four.
 *
 * Owner, 2026-08-25: «центральная часть скроллится, а так быть не должно, может
 * скроллиться только область где посылки». The detail view's middle column
 * carries strictly more than the create modal's does — ДОСТАВКА + ГРУЗ + КЛИЕНТ
 * + the driver-notes strip + the parcels block — against the same track height,
 * and measured at 1280 on order 94 its content ran 74px past the track. The
 * column's `overflow-y-auto` safety net then engaged and the COLUMN scrolled,
 * which is exactly what the owner is objecting to.
 *
 * The parcels list is the element designed to absorb that: it is the only one
 * with a fixed height and its own scroll, precisely so nothing else has to give.
 * Two rows rather than three because the deficit is 74px and a row costs
 * `PARCEL_ROW_H + ROW_GAP` = 42px: dropping one row recovers 42px and still
 * leaves the column 32px over, while dropping two recovers 84px and clears it.
 * Removing the duplicated barcode caption in the footer gives back a further
 * ~24px of margin on top.
 *
 * The create modal keeps four: its middle column carries less, so the budget
 * there genuinely affords them, and shrinking it would be cosmetic damage to a
 * surface that has no problem.
 */
export const PARCEL_DETAIL_VISIBLE_ROWS = 2;
export const PARCEL_DETAIL_LIST_H =
  PARCEL_ROW_H * PARCEL_DETAIL_VISIBLE_ROWS +
  ROW_GAP * (PARCEL_DETAIL_VISIBLE_ROWS - 1);

/** One input cell. Exported so both surfaces style their inputs identically. */
export const parcelCellCls =
  "h-9 w-full rounded-lg border border-gray-200 px-2.5 text-sm text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary";

interface Props {
  /** Barcode / code cell — an `<input>` supplied by the host. */
  code: ReactNode;
  /** Description cell. */
  description: ReactNode;
  /** Weight cell. */
  weight: ReactNode;
  /** Trailing control (remove / delete). */
  action?: ReactNode;
  /** Per-row validation message, rendered under the row. */
  error?: string | null;
  className?: string;
}

/**
 * The LAYOUT of one parcel row, shared by the create modal and the detail view.
 *
 * Deliberately presentational: it owns the grid tracks and the error slot, and
 * nothing else. The two surfaces differ in how a row is *wired* — the create
 * modal registers its inputs into a react-hook-form field array and only builds
 * form state, while the detail view holds local state and PERSISTS each row to
 * the server — so sharing the wiring would mean one component carrying two data
 * models. Sharing the markup is what stops the two rows drifting apart visually;
 * that is the part that actually drifted before.
 */
export function ParcelEditRow({
  code,
  description,
  weight,
  action,
  error,
  className,
}: Props) {
  return (
    <div className={className}>
      <div
        className="grid items-center gap-1.5"
        style={{ gridTemplateColumns: PARCEL_GRID_COLUMNS }}
      >
        {code}
        {description}
        {weight}
        {action ?? <span />}
      </div>
      {error && <p className="mt-1 text-xs text-brand-danger">{error}</p>}
    </div>
  );
}
